/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/url/ParseQueryParameters.ts
 * @create:      2025-06-28 09:10:40.934
 * @modify:      2025-07-11 21:19:12.799
 * @version:     6.0.0
 * @times:       9
 * @lines:       80
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TStringObject, TStringOrUndefined, TUriDecodeFunction } from "../type/basic";
import { GetQueryStringFromURL } from "./GetQueryStringFromURL";

/**
 * Parses the query parameters from a given URL string and returns them as an object.
 *
 * @param url - The URL string from which to extract query parameters. If the string does not contain a query part, it is treated as the query string itself.
 * @param uriDecodeMethod - (Optional) A function to decode URI components. If provided, both parameter names and values will be decoded using this method.
 * @returns An object mapping parameter names to their corresponding values, or `undefined` if a parameter has no value.
 *
 * @example
 * ```typescript
 * const params = ParseQueryParameters('https://example.com/?foo=bar&baz');
 * // params = { foo: 'bar', baz: undefined }
 * ```
 * 
 * @since 2.0.7
 * @version 2020-06-24
 */
export function ParseQueryParameters(url: string, uriDecodeMethod?: TUriDecodeFunction) {
    const query = GetQueryStringFromURL(url) || url;
    const items = query.split("&");
    const parameters: TStringObject<TStringOrUndefined> = {};

    const Add = (name: string, val?: string) => {
        if (uriDecodeMethod) {
            if (val !== undefined) {
                parameters[uriDecodeMethod(name)] = uriDecodeMethod(val);
            } else {
                parameters[uriDecodeMethod(name)] = undefined;
            }
        } else {
            parameters[name] = val;
        }
    };

    for (const item of items) {
        const parts = item.split("=");
        if (parts.length === 1) {
            Add(parts[0], undefined);
        } else {
            Add(parts[0], parts[1]);
        }
    }

    return parameters;
}
